import React from 'react';
import { Link } from 'react-router-dom';
import { routesPaths } from 'constants/routesPaths';
import './MockCreatorFinancePage.scss';

const headlineMetrics = [
  { label: 'League GMV (season)', value: '$1.84M', hint: 'Tickets, PPV, subscriptions, sponsors' },
  {
    label: 'Net league share',
    value: '$412K',
    hint: 'After creator and platform allocations',
    accent: true
  },
  { label: 'Active teams', value: '18', hint: 'Across 3 divisions' },
  { label: 'Upcoming events', value: '26', hint: 'Next 30 days' },
  { label: 'Sponsor delivery', value: '97.8%', hint: 'Booked vs delivered inventory' }
];

const seasonOps = [
  { label: 'Games scheduled', value: '142' },
  { label: 'Broadcast-ready events', value: '136' },
  { label: 'Needs staffing', value: '4' },
  { label: 'Venue issues', value: '2' },
  { label: 'Season health', value: 'On track', strong: true }
];

const teams = [
  {
    team: 'Elite FC',
    manager: 'Jamie Brooks',
    creators: '6',
    revenue: '$284K',
    readiness: 'Ready'
  },
  {
    team: 'North City Women',
    manager: 'Ava Patel',
    creators: '4',
    revenue: '$161K',
    readiness: 'Review'
  },
  {
    team: 'Riverside Academy',
    manager: 'Noah Kim',
    creators: '3',
    revenue: '$98K',
    readiness: 'Ready'
  }
];

const sponsorInventory = [
  {
    package: 'Matchday title sponsor',
    owner: 'Peak Sports',
    delivery: '100%',
    revenue: '$92K',
    status: 'Live'
  },
  {
    package: 'Halftime slot',
    owner: 'Hydra Energy',
    delivery: '83%',
    revenue: '$34K',
    status: 'Pacing'
  },
  {
    package: 'Replay pre-roll',
    owner: 'City Bank',
    delivery: '61%',
    revenue: '$22K',
    status: 'Watch'
  }
];

const audienceMetrics = [
  { label: 'Season pass buyers', value: '8,412', hint: '+12% vs last month' },
  { label: 'Repeat event buyers', value: '61%', hint: 'Cross-event retention' },
  { label: 'Highest conversion market', value: 'Texas', hint: '14.2% live-to-paid' },
  { label: 'CRM campaign lift', value: '9.8%', hint: 'Post-match win-back flow' }
];

const contentApprovals = [
  { item: 'Approve highlight package - Week 8', owner: 'Media Ops', status: 'Pending' },
  { item: 'Review sponsor overlay for finals', owner: 'Brand team', status: 'Review' },
  { item: 'Publish youth division recap', owner: 'League editor', status: 'Ready' }
];

const payoutOps = [
  { line: 'Creator payouts pending', value: '$188K' },
  { line: 'Team settlements pending', value: '$96K' },
  { line: 'Sponsor receivables open', value: '$54K' },
  { line: 'Failed payouts needing action', value: '2' }
];

const permissions = [
  { role: 'League admin', count: '4 users', rights: 'Billing, events, approvals' },
  { role: 'Club operator', count: '18 users', rights: 'Team events, roster, CRM' },
  { role: 'Media manager', count: '7 users', rights: 'Publishing, highlights, sponsor assets' }
];

const queue = [
  {
    id: 'ORG-204',
    issue: 'Finals venue stream check',
    owner: 'Broadcast ops',
    sla: '19 min',
    priority: 'High'
  },
  {
    id: 'ORG-198',
    issue: 'Sponsor make-good request',
    owner: 'Partnerships',
    sla: '44 min',
    priority: 'Medium'
  },
  {
    id: 'ORG-191',
    issue: 'Club payout exception',
    owner: 'Finance',
    sla: '12 min',
    priority: 'High'
  }
];

const whiteLabel = [
  { title: 'Brand kit', detail: 'Primary colors, logo lockup, lower thirds ready' },
  { title: 'Custom domain', detail: 'league.identifi.com active' },
  { title: 'Sponsor templates', detail: '3 approved layouts for live and replay' }
];

const auditTrail = [
  {
    date: 'Today',
    event: 'Revenue-share override approved for finals weekend',
    owner: 'League finance'
  },
  { date: 'Apr 14', event: 'Sponsor package inventory reallocated', owner: 'Partnerships lead' },
  { date: 'Apr 13', event: 'Club operator permissions updated', owner: 'League admin' }
];

const fallbackStyles = {
  page: {
    minHeight: '100vh',
    background: '#0c0c0f',
    color: '#e8e8ec',
    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif'
  },
  topbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '16px 24px',
    borderBottom: '1px solid #243246',
    background: '#101724'
  },
  shell: {
    display: 'grid',
    gridTemplateColumns: '220px 1fr'
  },
  nav: {
    padding: '24px 18px',
    borderRight: '1px solid #243246',
    background: '#101724'
  },
  main: {
    padding: '24px 28px 40px'
  },
  title: {
    color: '#fff',
    fontSize: '32px',
    margin: '0 0 8px'
  },
  subtitle: {
    color: '#a0a0b0',
    maxWidth: '760px',
    marginBottom: '24px'
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: '12px',
    marginBottom: '16px'
  },
  card: {
    background: '#16161d',
    border: '1px solid #2a2a34',
    borderRadius: '12px',
    padding: '18px'
  },
  panel: {
    background: '#16161d',
    border: '1px solid #2a2a34',
    borderRadius: '12px',
    padding: '18px',
    marginBottom: '16px'
  },
  tableWrap: {
    background: '#16161d',
    border: '1px solid #2a2a34',
    borderRadius: '12px',
    padding: '8px 18px',
    overflowX: 'auto'
  },
  link: {
    color: '#7dd3fc'
  },
  alert: {
    background: '#0f3b4b55',
    border: '1px solid #0f6d8b',
    borderRadius: '10px',
    color: '#bae6fd',
    padding: '12px 14px',
    marginBottom: '16px'
  }
};

const MockOrganizationDashboardPage = () => (
  <div className="mock-program-ui mock-program-ui--organization" style={fallbackStyles.page}>
    <header className="mock-program-ui__topbar" style={fallbackStyles.topbar}>
      <div className="mock-program-ui__brand">
        <Link
          to={routesPaths.home}
          className="mock-program-ui__link mock-program-ui__link--back"
          style={{ ...fallbackStyles.link, marginRight: 14, fontSize: 13 }}
          title="Back to Identifi home"
        >
          &larr; Home
        </Link>
        <Link
          to={routesPaths.home}
          className="mock-program-ui__logo-link"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <span className="mock-program-ui__logo">Elite League HQ</span>
        </Link>
        <span className="mock-program-ui__badge mock-program-ui__badge--admin">
          Prototype UI - organization / league workspace
        </span>
      </div>
      <div className="mock-program-ui__topbar-right">
        <span className="mock-program-ui__user">Commissioner Ops / eliteleague</span>
        <Link
          className="mock-program-ui__link"
          style={fallbackStyles.link}
          to={routesPaths.mockCreatorFinance}
        >
          Creator mock &rarr;
        </Link>
      </div>
    </header>

    <div className="mock-program-ui__shell" style={fallbackStyles.shell}>
      <aside className="mock-program-ui__nav" style={fallbackStyles.nav}>
        <p className="mock-program-ui__nav-label">Organization</p>
        <button
          type="button"
          className="mock-program-ui__nav-item mock-program-ui__nav-item--active"
        >
          Overview
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Season operations
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Teams and permissions
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Revenue and settlements
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Sponsors and ad ops
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Audience and CRM
        </button>
        <button type="button" className="mock-program-ui__nav-item">
          Brand and white-label
        </button>
      </aside>

      <main className="mock-program-ui__main" style={fallbackStyles.main}>
        <div className="mock-program-ui__toolbar">
          <div className="mock-program-ui__toolbar-group">
            <span className="mock-program-ui__toolbar-chip mock-program-ui__toolbar-chip--active">
              Season to date
            </span>
            <span className="mock-program-ui__toolbar-chip">All divisions</span>
            <span className="mock-program-ui__toolbar-chip">League currency</span>
          </div>
          <div className="mock-program-ui__toolbar-group">
            <button type="button" className="mock-program-ui__btn mock-program-ui__btn--ghost">
              Export league report
            </button>
            <button type="button" className="mock-program-ui__btn">
              Create event batch
            </button>
          </div>
        </div>

        <h1 className="mock-program-ui__title" style={fallbackStyles.title}>
          Organization and league operations
        </h1>
        <p className="mock-program-ui__subtitle" style={fallbackStyles.subtitle}>
          The operator layer between creator and platform admin: multi-team event management,
          org-level settlements, sponsor delivery, approvals, and white-label controls.
        </p>

        <div
          className="mock-program-ui__alert mock-program-ui__alert--ok"
          style={fallbackStyles.alert}
        >
          <strong>Season status</strong>
          <span>
            {' '}
            96% of scheduled events are broadcast-ready, sponsor inventory is pacing ahead of plan,
            and no league-wide payout blockers are open.
          </span>
        </div>

        <div
          className="mock-program-ui__cards mock-program-ui__cards--admin"
          style={fallbackStyles.cards}
        >
          {headlineMetrics.map(metric => (
            <div
              key={metric.label}
              className={`mock-program-ui__card${
                metric.accent ? ' mock-program-ui__card--accent-admin' : ''
              }`}
              style={fallbackStyles.card}
            >
              <span className="mock-program-ui__card-label">{metric.label}</span>
              <strong className="mock-program-ui__card-value">{metric.value}</strong>
              <span className="mock-program-ui__card-hint">{metric.hint}</span>
            </div>
          ))}
        </div>

        <div className="mock-program-ui__section-grid mock-program-ui__section-grid--two">
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Season operations</h2>
            <div className="mock-program-ui__metric-list">
              {seasonOps.map(item => (
                <div
                  key={item.label}
                  className={`mock-program-ui__metric-row${
                    item.strong ? ' mock-program-ui__metric-row--strong' : ''
                  }`}
                >
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <div className="mock-program-ui__panel-header">
              <h2 className="mock-program-ui__panel-title">League settlement stack</h2>
              <span className="mock-program-ui__micro-label">Finance ops</span>
            </div>
            <div className="mock-program-ui__metric-list">
              {payoutOps.map(item => (
                <div key={item.line} className="mock-program-ui__metric-row">
                  <span>{item.line}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
          <div className="mock-program-ui__panel-header">
            <h2 className="mock-program-ui__panel-title">Teams and operating readiness</h2>
            <button type="button" className="mock-program-ui__btn">
              Invite operator
            </button>
          </div>
          <div className="mock-program-ui__table-wrap" style={fallbackStyles.tableWrap}>
            <table className="mock-program-ui__table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Manager</th>
                  <th>Creators</th>
                  <th>Revenue</th>
                  <th>Readiness</th>
                </tr>
              </thead>
              <tbody>
                {teams.map(item => (
                  <tr key={item.team}>
                    <td>{item.team}</td>
                    <td>{item.manager}</td>
                    <td>{item.creators}</td>
                    <td>{item.revenue}</td>
                    <td>{item.readiness}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mock-program-ui__section-grid mock-program-ui__section-grid--two">
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <div className="mock-program-ui__panel-header">
              <h2 className="mock-program-ui__panel-title">Sponsor inventory and delivery</h2>
              <span className="mock-program-ui__micro-label">Ad ops</span>
            </div>
            <div className="mock-program-ui__table-wrap" style={fallbackStyles.tableWrap}>
              <table className="mock-program-ui__table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Owner</th>
                    <th>Delivery</th>
                    <th>Revenue</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sponsorInventory.map(item => (
                    <tr key={item.package}>
                      <td>{item.package}</td>
                      <td>{item.owner}</td>
                      <td>{item.delivery}</td>
                      <td>{item.revenue}</td>
                      <td>{item.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <div className="mock-program-ui__panel-header">
              <h2 className="mock-program-ui__panel-title">Audience and CRM</h2>
              <span className="mock-program-ui__micro-label">Growth</span>
            </div>
            <div className="mock-program-ui__mini-grid">
              {audienceMetrics.map(item => (
                <div key={item.label} className="mock-program-ui__mini-stat">
                  <span className="mock-program-ui__mini-label">{item.label}</span>
                  <strong className="mock-program-ui__mini-value">{item.value}</strong>
                  <span className="mock-program-ui__mini-hint">{item.hint}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mock-program-ui__section-grid mock-program-ui__section-grid--two">
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Content approvals</h2>
            <div className="mock-program-ui__checklist">
              {contentApprovals.map(item => (
                <div key={item.item} className="mock-program-ui__check-item">
                  <div>
                    <strong>{item.item}</strong>
                    <span>{item.owner}</span>
                  </div>
                  <span className="mock-program-ui__micro-label">{item.status}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Roles and permissions</h2>
            <div className="mock-program-ui__checklist">
              {permissions.map(item => (
                <div key={item.role} className="mock-program-ui__check-item">
                  <div>
                    <strong>{item.role}</strong>
                    <span>{item.count}</span>
                  </div>
                  <span>{item.rights}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mock-program-ui__section-grid mock-program-ui__section-grid--two">
          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Operations queue</h2>
            <div className="mock-program-ui__table-wrap" style={fallbackStyles.tableWrap}>
              <table className="mock-program-ui__table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Issue</th>
                    <th>Owner</th>
                    <th>SLA</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map(item => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.issue}</td>
                      <td>{item.owner}</td>
                      <td>{item.sla}</td>
                      <td>{item.priority}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
            <h2 className="mock-program-ui__panel-title">Brand and white-label</h2>
            <div className="mock-program-ui__checklist">
              {whiteLabel.map(item => (
                <div key={item.title} className="mock-program-ui__check-item">
                  <div>
                    <strong>{item.title}</strong>
                    <span>{item.detail}</span>
                  </div>
                  <button
                    type="button"
                    className="mock-program-ui__btn mock-program-ui__btn--ghost"
                  >
                    Manage
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mock-program-ui__panel" style={fallbackStyles.panel}>
          <h2 className="mock-program-ui__panel-title">Audit trail</h2>
          <div className="mock-program-ui__timeline">
            {auditTrail.map(item => (
              <div key={item.date + item.event} className="mock-program-ui__timeline-item">
                <span className="mock-program-ui__timeline-date">{item.date}</span>
                <div>
                  <strong>{item.event}</strong>
                  <span>{item.owner}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  </div>
);

export default MockOrganizationDashboardPage;
